//@flow
import * as React from 'react';
import { translate } from 'react-i18next';
import slotSelectorModalFactory from 'common/entity/components/slot/SlotsSelectorModalFactory';
import TimeSlotsSelector from 'common/entity/components/multiDaySlot/TimeSlotsSelector';
import { MultiDaySlot } from 'common/models/internal/MultiDaySlot';
import { TimeSlot } from 'common/models/internal/TimeSlot';
import { DayOfWeekType } from 'common/enums/DateTimeEnums';
import { SlotType, type SlotTypeEnumType } from 'common/enums/SlotEnum';
import moment from 'moment-timezone';
import ArrayUtil from 'common/utils/ArrayUtil';
import DateTimeUtils, { CustomDateFormats } from 'common/utils/DateTimeUtils';

type Props = {
    className?: string,
    theme: Object,
    defaultSlots: ?Array<MultiDaySlot>,
    onSlotApply: (Array<MultiDaySlot>) => void,
    onDismiss: (?boolean) => void
};

export const TimeSlotsSelectorModal = slotSelectorModalFactory({
    SlotSelectorSection: TimeSlotsSelector
});

export const deriveSlotType = function (
    allSlots: Array<MultiDaySlot>
): $Values<typeof SlotType> {
    if (allSlots.length > 7) {
        throw new Error(`Slots length higher than 7: ${allSlots.length}`);
    }
    if (allSlots.length === 0) {
        //Default
        return SlotType.EVERYDAY;
    }
    if (allSlots.length < 7) {
        return SlotType.CUSTOM;
    }
    const [firstDay, ...otherDays] = allSlots;
    for (const day of otherDays) {
        const { slots: timeSlots } = day;
        if (timeSlots.size !== firstDay.slots.size) {
            return SlotType.CUSTOM;
        }
        const isEqual = timeSlots.every((slot, index) => {
            const firstDaySlot = firstDay.slots.get(index);
            return slot.equals(firstDaySlot);
        });
        if (!isEqual) {
            return SlotType.CUSTOM;
        }
    }
    return SlotType.EVERYDAY;
};

class TimeSlotsSelectorModalContainer extends React.Component<Props> {
    static defaultFormat = 'HH:mm';

    addSlot = ({
        timeSlots,
        slotFor
    }: {
        timeSlots: Array<MultiDaySlot>,
        slotFor: string
    }) => {
        const timeSlotIndex = timeSlots.findIndex(
            timeSlot => timeSlot.dayOfWeek === slotFor
        );
        const timeSlot = timeSlots[timeSlotIndex];
        const updatedTimeSlot = timeSlot.setSlots(
            timeSlot.slots.push(TimeSlot.fromJS(this.createDefaultSlot()))
        );
        return ArrayUtil.insertItemAtIndex({
            array: timeSlots,
            index: timeSlotIndex,
            item: updatedTimeSlot
        });
    };

    syncSlots = ({
        timeSlots,
        slotType
    }: {
        timeSlots: Array<MultiDaySlot>,
        slotType: SlotTypeEnumType
    }): Array<MultiDaySlot> => {
        if (slotType === SlotType.EVERYDAY) {
            // When switched from custom slot to everyday slot, first day's
            // slots are retained. Hence copy first day slots to all days.
            const [{ dayOfWeek }] = timeSlots;
            return this.copySlotToAll({ timeSlots, slotFor: dayOfWeek });
        }
        return timeSlots;
    };

    copySlotToAll = ({
        timeSlots,
        slotFor
    }: {
        timeSlots: Array<MultiDaySlot>,
        slotFor: string
    }) => {
        const sourceTimeslot = timeSlots.find(
            timeSlot => timeSlot.dayOfWeek === slotFor
        );
        const updatedTimeSlots = sourceTimeslot
            ? timeSlots.map(timeSlot => {
                  return timeSlot.setSlots(sourceTimeslot.slots);
              })
            : timeSlots;
        return updatedTimeSlots;
    };

    getUpdatedTimeSlots({
        timeSlots,
        slotFor,
        start,
        end,
        index
    }: {
        timeSlots: Array<MultiDaySlot>,
        slotFor: string,
        end: moment,
        start: moment,
        index: number
    }) {
        const timeSlotIndex = timeSlots.findIndex(
            timeSlot => timeSlot.dayOfWeek === slotFor
        );
        const timeSlot = timeSlots[timeSlotIndex];
        const slotToBeUpdated = timeSlot.slots.get(index);
        const updatedSlot = slotToBeUpdated
            .setStart(
                DateTimeUtils.customFormat(start, CustomDateFormats.API_TIME)
            )
            .setEnd(
                DateTimeUtils.customFormat(end, CustomDateFormats.API_TIME)
            );
        const updatedTimeSlot = timeSlot.setSlots(
            timeSlot.slots.set(index, updatedSlot)
        );
        return ArrayUtil.insertItemAtIndex({
            array: timeSlots,
            index: timeSlotIndex,
            item: updatedTimeSlot
        });
    }

    deleteSingleSlot({
        timeSlots,
        slotFor,
        index
    }: {
        timeSlots: Array<MultiDaySlot>,
        slotFor: string,
        index: number
    }) {
        const timeSlotIndex = timeSlots.findIndex(
            timeSlot => timeSlot.dayOfWeek === slotFor
        );
        const timeSlot = timeSlots[timeSlotIndex];
        const updatedSlot = timeSlot.slots.delete(index);
        const updatedTimeSlot = timeSlot.setSlots(updatedSlot);
        return ArrayUtil.insertItemAtIndex({
            array: timeSlots,
            index: timeSlotIndex,
            item: updatedTimeSlot
        });
    }

    createInitialSlots = (defaultSlots: ?Array<MultiDaySlot>) => {
        const slots = [];
        Object.values(DayOfWeekType).forEach(day => {
            let existingSlot;
            if (defaultSlots) {
                existingSlot = defaultSlots.find(
                    slot => slot.dayOfWeek === day
                );
            }
            slots.push(
                MultiDaySlot.fromJS({
                    dayOfWeek: day,
                    slots: existingSlot
                        ? existingSlot.slots.toJS()
                        : [this.createDefaultSlot()]
                })
            );
        });
        return slots;
    };

    deriveSelectedDays(defaultSlots: ?Array<MultiDaySlot>) {
        if (!defaultSlots || defaultSlots.length === 0) {
            return Object.values(DayOfWeekType);
        }
        return defaultSlots.map(slot => slot.dayOfWeek);
    }

    validateTimeSlot = (timeSlot: MultiDaySlot) => {
        return !timeSlot.getValidationMessage('slots');
    };

    createDefaultSlot = () => {
        return {
            start: '08:00',
            end: '18:00'
        };
    };

    getIsTimeSlotEmpty(timeSlot: MultiDaySlot) {
        return timeSlot.slots.size === 0;
    }

    getShouldUncheckOnRemove(timeSlot: MultiDaySlot) {
        // Last slot is getting removed, so uncheck the selection
        return timeSlot.slots.size === 1;
    }

    render() {
        const {
            className,
            theme,
            onDismiss,
            t,
            onSlotApply,
            defaultSlots
        } = this.props;
        return (
            <TimeSlotsSelectorModal
                theme={theme}
                title={t('translations:chooseSlot')}
                className={className}
                defaultSlots={defaultSlots}
                onSlotApply={onSlotApply}
                onDismiss={onDismiss}
                validateTimeSlot={this.validateTimeSlot}
                addSlot={this.addSlot}
                copySlotToAll={this.copySlotToAll}
                getUpdatedTimeSlots={this.getUpdatedTimeSlots}
                deleteSingleSlot={this.deleteSingleSlot}
                deriveSlotType={deriveSlotType}
                createDefaultSlot={this.createDefaultSlot}
                createInitialSlots={this.createInitialSlots}
                deriveSelectedDays={this.deriveSelectedDays}
                getShouldUncheckOnRemove={this.getShouldUncheckOnRemove}
                getIsTimeSlotEmpty={this.getIsTimeSlotEmpty}
                syncSlots={this.syncSlots}
            />
        );
    }
}

export { TimeSlotsSelectorModalContainer as UndecoratedTimeSlotsSelectorModalContainer };
export default translate('entityManagement')(TimeSlotsSelectorModalContainer);
